import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api.js';

/**
 * How much of this dealership's own setup is finished.
 *
 * ONE QUERY KEY, FOUR SURFACES. The dashboard banner, the percentage tile, the
 * wizard modal and the once-a-session login prompt all read this. They quote
 * the same number at the same moment because they share a cache entry — the
 * lesson useListingHealth records, where the Inventory button counted the
 * loaded page while the screen it opened counted the tenant.
 *
 * `enabled` is the permission gate. /setup/summary requires settings.edit and
 * 403s without it, so calling it for a salesperson would be a guaranteed
 * failed request on every page load — and a checklist shown to someone who
 * cannot open Settings is a list of things they must go and ask for.
 */
export function useSetupCompletion({ enabled = true } = {}) {
    return useQuery({
        queryKey: ['setup-completion'],
        enabled,
        //Short, because unlike listing health this changes by hand: someone
        //walks to Settings, fills a field and comes back expecting the number
        //to have moved. Settings also invalidates this key on unmount, so the
        //stale window only covers edits made outside it.
        staleTime: 60 * 1000,
        //A failure here must not retry in a loop behind a prompt nobody asked
        //for. One attempt, then every surface renders nothing.
        retry: false,
        queryFn: async () => {
            const res = await apiFetch('/setup/summary', { method: 'GET' });
            const body = await res.json().catch(() => null);
            if (!res.ok || body?.ok !== true) {
                throw new Error(body?.message || 'Failed to load setup status');
            }
            return body;
        },
    });
}

export default useSetupCompletion;
